import React, { useContext, useEffect, useRef } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import RouteLogger from "./routeLogger";
import { trackEvent } from "./Functions/analytics";
import { AuthContext } from "./Firebase/AuthProvider";

// Pages an unverified email account can still open: the login page itself,
// the policies it links to, and an order page (the Firestore rules decide
// what it shows; the admin panel opens it to view a customer's order).
const GATE_FREE = [/^\/login/, /^\/terms/, /^\/privacy/, /^\/shipping/, /^\/cancellation/, /^\/refund/, /^\/returns/, /^\/order\//];

function UnverifiedEmailGate() {
  const { user, running } = useContext(AuthContext);
  const location = useLocation();

  if (running || !user || GATE_FREE.some((re) => re.test(location.pathname))) return null;

  const usesPassword = user.providerData?.some(
    (entry) => entry.providerId === "password",
  );
  if (!usesPassword || !user.email || user.emailVerified) return null;

  return <Navigate to="/login" replace />;
}

const scrollPositions = new Map();

function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousKeyRef = useRef(location.key);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = "auto";
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousKey = previousKeyRef.current;
    scrollPositions.set(previousKey, { x: window.scrollX, y: window.scrollY });
    previousKeyRef.current = location.key;

    // Same page, new query (filters, sort, search): stay where the shopper
    // is. Jumping to the top made picking a second filter a chore.
    const samePage = previousPathRef.current === location.pathname;
    previousPathRef.current = location.pathname;
    if (samePage && navigationType !== "POP") return undefined;

    const restore = () => {
      if (navigationType === "POP") {
        const saved = scrollPositions.get(location.key);
        window.scrollTo(saved?.x || 0, saved?.y || 0);
      } else if (!location.hash) {
        window.scrollTo(0, 0);
      }
    };

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(restore);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.key, location.hash, navigationType]);

  return null;
}

/**
 * One page_view per route the visitor actually lands on.
 *
 * Keyed on pathname alone: the query string carries design tokens and filter
 * state that change as someone customises a garment, and counting each of
 * those as another page view would make the busiest page on the site look
 * like the most popular one.
 */
// The customizer keeps the whole design in its last path segment
// (/custom/kurta/select_fabric/personalize/<design>), which changes with every
// fabric or style picked: one page, not a page view per change (each one was
// two Firestore writes on the customer's phone).
const pageKey = (pathname) => {
  const parts = String(pathname || "/").split("/");
  return parts[1] === "custom" && parts.length > 5 ? parts.slice(0, 5).join("/") : pathname;
};

function PageViews() {
  const location = useLocation();
  const lastPath = useRef(null);
  const key = pageKey(location.pathname);

  useEffect(() => {
    if (lastPath.current === key) return;
    const from = lastPath.current || "";
    lastPath.current = key;
    trackEvent("page_view", { from });
  }, [key]);

  return null;
}

function App() {
  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const url = window.location.href;

    if (userAgent.includes('Mobile') && (userAgent.includes('iPhone') || userAgent.includes('iPad')) && userAgent.includes("/FBAN|FBAV/i")) {
      window.location.href = 'x-safari-' + url;
      return;
    }
  }, []);

  return (
    <div className="App">
      <ScrollRestoration />
      <RouteLogger/>
      <PageViews />
      <UnverifiedEmailGate />
      <Outlet />
    </div>
  );
}

export default App;
